import type { AlertAction, AlertListQuery } from '@convometry/contracts';
import { useToast } from '@convometry/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../app/session.js';
import { api, ApiError, errorMessage } from '../lib/api.js';
import { usePollingInterval } from '../lib/live.js';
import { qk } from '../lib/query.js';

export function useAlertSummary() {
  const { tenantId } = useTenant();
  // Live updates come through SSE; poll slowly only as a fallback.
  const refetchInterval = usePollingInterval(30_000);
  return useQuery({ queryKey: qk.alertSummary(tenantId), queryFn: () => api('alertSummary', { params: { tenantId } }), refetchInterval });
}

export function useAlertList(query: Partial<AlertListQuery>, enabled = true) {
  const { tenantId } = useTenant();
  const refetchInterval = usePollingInterval(30_000);
  return useQuery({ queryKey: qk.alerts(tenantId, query), queryFn: () => api('listAlerts', { params: { tenantId }, query }), refetchInterval, enabled });
}

const DONE: Record<AlertAction['action'], string> = { acknowledge: 'Alerta marcada como vista', snooze: 'Alerta pospuesta', resolve: 'Alerta resuelta' };

export function useAlertAction() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (v: { alertId: string; action: AlertAction }) => api('alertAction', { params: { tenantId, alertId: v.alertId }, body: v.action }),
    onSuccess: (data, v) => {
      qc.setQueryData(qk.alert(tenantId, data.id), data);
      void qc.invalidateQueries({ queryKey: qk.alerts(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.alertSummary(tenantId) });
      toast({ title: DONE[v.action.action], tone: 'success' });
    },
    onError: (err) => {
      const stale = err instanceof ApiError && err.code === 'alert_transition';
      if (stale) {
        void qc.invalidateQueries({ queryKey: qk.alerts(tenantId) });
        void qc.invalidateQueries({ queryKey: qk.alertSummary(tenantId) });
      }
      toast({ title: stale ? 'La alerta cambió' : 'No se pudo actualizar la alerta', description: errorMessage(err), tone: 'danger' });
    },
  });
}
